import { useCallback, useState, useRef } from 'react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { scrollToTranscriptCard } from './utils/scroll-to-transcript-card';

interface TranscriptSearchProps {
  resultIndexs: number[];
  showArrows: boolean;
  handleSearchInput: (event: React.FormEvent<HTMLInputElement>) => void;
  handleClear: () => void;
}

export function TranscriptSearch(props: TranscriptSearchProps) {
  const { resultIndexs, showArrows, handleSearchInput, handleClear } = props;
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleArrowClick = useCallback(
    (dir: number) => {
      let index = currentIndex + dir;
      if (index <= 0) {
        index = 0;
      }
      if (index > resultIndexs.length - 1) {
        index = resultIndexs.length - 1;
      }
      setCurrentIndex(index);
      scrollToTranscriptCard(resultIndexs[index]);
    },
    [setCurrentIndex, currentIndex, resultIndexs]
  );

  const handleClearInput = useCallback(() => {
    handleClear();
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [handleClear]);

  const arrowUpDisabled = currentIndex === 0;
  const arrowDownDisabled = currentIndex === resultIndexs.length - 1;

  return (
    <div
      data-ref="transcript-search"
      className="flex border border-neutral-200 rounded-[4px] h-[32px] items-center px-[9px] bg-white mb-[10px] mr-[16px] transition-colors duration-250 focus-within:border-cyan-700"
    >
      <Icon icon={IconsMap.SEARCH} className="w-[16px] h-[16px] text-neutral-500 mr-[8px] flex-shrink-0" />
      <input
        ref={inputRef}
        type="text"
        placeholder="Search ..."
        className="text-[12px] w-full outline-none text-neutral-800 font-normal"
        onInput={handleSearchInput}
      ></input>
      {showArrows ? (
        <div className="flex ml-[8px]">
          {resultIndexs.length > 1 ? (
            <>
              <button
                onClick={() => handleArrowClick(1)}
                className={classNames('mr-[8px]', arrowDownDisabled ? 'opacity-50' : '')}
                disabled={arrowDownDisabled}
              >
                <Icon
                  icon={IconsMap.ARROW_2_UP}
                  className={classNames(
                    'w-[12px] h-[12px] rotate-180',
                    arrowDownDisabled ? 'text-neutral-500' : 'text-cyan-700'
                  )}
                />
              </button>
              <button
                onClick={() => handleArrowClick(-1)}
                className={classNames('mr-[8px]', arrowUpDisabled ? 'opacity-50' : '')}
                disabled={arrowUpDisabled}
              >
                <Icon
                  icon={IconsMap.ARROW_2_UP}
                  className={classNames('w-[12px] h-[12px]', arrowUpDisabled ? 'text-neutral-500' : 'text-cyan-700')}
                />
              </button>

              <div className="w-[1px] h-[10px] mr-[8px] border-r border-neutral-200"></div>
            </>
          ) : null}
          <button onClick={handleClearInput}>
            <Icon icon={IconsMap.CLOSE} className="w-[12px] h-[12px] text-black opacity-[0.54]" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
